import React, { useEffect, useCallback } from "react"
import { useLazyQuery } from "@apollo/client/react"
import {
  OrderWorkflowExecutionSearchHit,
  SearchOrderWorkflowExecutionsDocument,
  SearchOrderWorkflowExecutionsQuery,
  SearchOrderWorkflowExecutionsQueryVariables,
  SortOrder
} from "lib/types/generated/graphql"

import WorkflowExecutionsTable from "./WorkflowExecutionsTable"
import ExecutionCanvas from "./ExecutionCanvas"

const PAGE_SIZE = 25

type Props = {
  workflowId: string
}

function ExecutionTable({ workflowId }: Props) {
  const [searchExecutions, { data, loading, error }] = useLazyQuery<
    SearchOrderWorkflowExecutionsQuery,
    SearchOrderWorkflowExecutionsQueryVariables
  >(SearchOrderWorkflowExecutionsDocument, {
    fetchPolicy: "network-only"
  })

  const fetchExecutions = useCallback(() => {
    searchExecutions({
      variables: {
        from: 0,
        size: PAGE_SIZE,
        sort: [{ field: "created", order: SortOrder.Desc }],
        query: JSON.stringify({
          bool: {
            must: [{ term: { workflowId } }]
          }
        })
      }
    })
  }, [searchExecutions, workflowId])

  useEffect(() => {
    fetchExecutions()
  }, [fetchExecutions])

  const hits: OrderWorkflowExecutionSearchHit[] =
    data?.searchOrderWorkflowExecutions?.hits ?? []

  return (
    <WorkflowExecutionsTable
      title="Latest executions"
      hits={hits}
      loading={loading}
      variant="by-workflow"
      emptyMessage="No executions found for this workflow"
      errorMessage="Failed to load executions"
      error={Boolean(error)}
      onRefresh={fetchExecutions}
      onRetryComplete={fetchExecutions}
      expansionContent={(hit) => <ExecutionCanvas executionId={hit.id} />}
    />
  )
}

export default ExecutionTable
